import { Trans, useLingui } from '@lingui/react/macro'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { getErrorMessage } from '@tshared/oapi/req/models'
import { App, Button, Form, Input, Result, Typography } from 'antd'
import { z } from 'zod'

import { useMobileRegisterMutation } from '@/apis/mobile-auth.api'
import { LockOutlined } from '@/icons'

import logo from '../-assets/logo.svg'
import { useFormValidation } from '../-hooks/useFormValidation'

const { Title } = Typography

export const Route = createFileRoute('/auth/activation/')({
  component: ActivationPage,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
})

type ActivationFormValues = {
  password: string
  confirmPassword: string
}

function ActivationPage() {
  const { t } = useLingui()
  const { message } = App.useApp()
  const { token } = Route.useSearch()
  const { getPasswordRules } = useFormValidation()
  const registerMutation = useMobileRegisterMutation()
  const navigate = useNavigate()

  if (!token) {
    return <Result status="error" title={t`Invalid or expired invitation link`} />
  }

  const onFinish = async (values: ActivationFormValues) => {
    try {
      await registerMutation.mutateAsync({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      })
      void message.success(t`Registration successful`)
      void navigate({
        to: '/auth/success',
        search: {
          success_type: 'register_success',
        },
      })
    } catch (error) {
      void message.error(
        getErrorMessage(error as Error, t`Registration failed`) ?? t`Registration failed`,
      )
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto', padding: '32px 0' }}>
      <div className="flex justify-center">
        <img src={logo} alt="" className="mb-8" />
      </div>
      <Title level={1} className="mb-1">
        <Trans>Complete Registration</Trans>
      </Title>
      <p className="mb-6 text-sm text-gray-500">
        <Trans>Set a password to activate your account</Trans>
      </p>

      <Form<ActivationFormValues> onFinish={onFinish} layout="vertical" size="large">
        <Form.Item name="password" rules={getPasswordRules(8)}>
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter password`} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: t`Please confirm password!` },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve()
                return Promise.reject(new Error(t`Passwords do not match!`))
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t`Enter password again`} />
        </Form.Item>

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={registerMutation.isPending}
            block
            size="large"
          >
            <Trans>Complete Registration</Trans>
          </Button>
        </Form.Item>
      </Form>
    </div>
  )
}
